// vuex
import { Store } from "vuex";
// firebase
import { firebase, getFirebaseMessagingToken, initFirebase } from "@/config/firebase";
// tipos
import { State } from "@/typings/store";
import { Usuario } from "@/typings/store/plugins/easyFirestore/usuarios";
// helpers
import { idtify } from "@/helpers/string";
// imports
import abrirCanalesBD from "./abrirCanalesBD";
import cerrarCanalesBD from "./cerrarCanalesBD";
import { Carrito } from "@/typings/store/plugins/easyFirestore/carritos";

const iniciarFirebase = (store: Store<State>): void => {
  initFirebase()
    .catch((error: unknown) => {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[Firebase] initFirebase falló; la UI sigue y onAuth se registrará igual.",
          error
        );
      }
    })
    .then(() => {
      if (!firebase.apps.length) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(
            "[Firebase] No hay app inicializada; no se registra auth hasta corregir configuración."
          );
        }
        store.commit("cargando/set", false);
        return;
      }
      try {
        firebase.auth().onAuthStateChanged(async (user) => {
        store.commit("cargando/set", true);
        if (user) {
          if (user.email) {
            await store
              .dispatch("usuarios/fetchById", user.uid)
              .catch((error) => {
                if (process.env.NODE_ENV !== "production") console.error(error);
              });
            const usuarioUID = store.getters["usuarios/get"][user.uid] as
              | Usuario
              | undefined;
            const id = idtify(user.email);
            await store.dispatch("usuarios/fetchById", id).catch((error) => {
              if (process.env.NODE_ENV !== "production") console.error(error);
            });
            const usuarioEmail = store.getters["usuarios/get"][id] as
              | Usuario
              | undefined;
            let usuario: Usuario | null = null;
            if (usuarioEmail) {
              usuario = Object.assign({}, usuarioUID, usuarioEmail);
              usuario.id = user.uid;
              usuario.uid = user.uid;
              await store.dispatch("usuarios/set", usuario);
              await store.dispatch("usuarios/delete", id);
            } else if (!usuarioUID) {
              const clienteUsuario: Usuario = {
                id: user.uid,
                uid: user.uid,
                rut: "",
                nombre: user.providerData[0]?.displayName ?? "",
                email: user.providerData[0]?.email ?? "",
                permisos: {
                  misCursos: true,
                  cursosDisponibles: true,
                  perfil: true,
                  notificaciones: true,
                  pagos: true,
                  agenda: true,
                },
                fecha: new Date().toISOString(),
                telefono: "",
                ciudad: null,
                tipo: "cliente",
                estado: "libre",
                region: "",
              };
              usuario = clienteUsuario;
              await store.dispatch("usuarios/set", clienteUsuario);
            }
            store.dispatch("carritos/fetchById", user.uid).catch((error) => {
              if (process.env.NODE_ENV !== "production") console.error(error);
            });
            const carrito = store.getters["carritos/get"][user.uid] as
              | Carrito
              | undefined;
            if (!carrito && usuario?.tipo === "cliente") {
              const carrito: Carrito = {
                id: user.uid,
                usuario: usuario,
                estado: "revisado",
                cursos: {},
                promocion: null,
                sesiones: {},
              };
              await store.dispatch("carritos/set", carrito);
            }
          }
          const usuario = store.getters["usuarios/get"][user.uid] as
            | Usuario
            | undefined;
          if (usuario?.estado === "bloqueado") {
            store.commit("setErrorLogin", "inhabilitado");
            store.commit("cargando/set", false);
            firebase.auth().signOut();
            return;
          }
          if (usuario) {
            const vapidKey =
              typeof process.env.VUE_APP_VAPID_KEY !== "undefined" && process.env.VUE_APP_VAPID_KEY !== ""
                ? process.env.VUE_APP_VAPID_KEY
                : "BMFqY0n8SMHXvMsNlfOHtz1vyuD-_FUI29yBXgRnM_W8Equf2K6ro34y2qMIHlwdxqQl0OI4yS1B7zuhpg5m0BI";
            usuario.token = await getFirebaseMessagingToken(vapidKey);
            await store.dispatch("usuarios/set", usuario);
          }
          store.commit("usuario/set", usuario ? usuario : null);
          // Si el usuario existe y no fue bloqueado (bloqueado ya hizo `return` arriba),
          // limpiamos cualquier error previo de login.
          if (usuario) store.commit("setErrorLogin", "");
          if (
            !usuario?.permisos ||
            Object.values(usuario.permisos).length === 0
          ) {
            firebase.auth().signOut();
            store.commit("cargando/set", false);
            return;
          }
          await abrirCanalesBD(store, usuario);
        } else {
          const usuario = store.getters["usuario/get"];
          if (usuario) {
            await cerrarCanalesBD(store);
          }
          store.commit("usuario/set", null);
        }
        setTimeout(() => {
          store.commit("cargando/set", false);
        }, 3000);
      });
      } catch (error: unknown) {
        if (process.env.NODE_ENV !== "production") {
          console.error("[Firebase] onAuthStateChanged no disponible.", error);
        }
        store.commit("cargando/set", false);
      }
    });
};

export default iniciarFirebase;
